import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { mealDeskCompaniesService, mealDeskVendorsService } from '../firebase/services';
import { 
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

const WeeklyScheduleViewer = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [conflictVendors, setConflictVendors] = useState([]);
  const [availableVendors, setAvailableVendors] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const companyDropdownRef = useRef(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyFormData, setCopyFormData] = useState({
    fromDate: '',
    toDate: '',
    targetDate: ''
  });

  // Meal types
  const mealTypes = [
    { key: 'breakfast', label: 'Breakfast', icon: '🌅' },
    { key: 'lunch', label: 'Lunch', icon: '🍽️' },
    { key: 'dinner', label: 'Dinner', icon: '🌙' }
  ];

  // Days of the week
  const daysOfWeek = [
    { key: 'monday', label: 'Monday', short: 'Mon' },
    { key: 'tuesday', label: 'Tuesday', short: 'Tue' },
    { key: 'wednesday', label: 'Wednesday', short: 'Wed' },
    { key: 'thursday', label: 'Thursday', short: 'Thu' },
    { key: 'friday', label: 'Friday', short: 'Fri' },
    { key: 'saturday', label: 'Saturday', short: 'Sat' },
    { key: 'sunday', label: 'Sunday', short: 'Sun' }
  ];

  // Get week dates (Monday to Sunday)
  const getWeekDates = (date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    start.setDate(diff);
    
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      weekDates.push(date.toISOString().split('T')[0]);
    }
    return weekDates;
  };

  // Load companies and vendors
  useEffect(() => {
    const loadData = async () => {
      try {
        const [companiesData, vendorsData] = await Promise.all([
          mealDeskCompaniesService.getAllCompanies(),
          mealDeskVendorsService.getAllVendors()
        ]);
        setCompanies(companiesData);
        setVendors(vendorsData);
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Load schedules when company or week changes
  useEffect(() => {
    if (!selectedCompany) return;

    const weekDates = getWeekDates(currentWeek);
    const startDate = weekDates[0];
    const endDate = weekDates[6];

    const schedulesQuery = query(
      collection(db, 'MealDeskVendorSchedule'),
      where('companyId', '==', selectedCompany),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date')
    );

    const unsubscribe = onSnapshot(schedulesQuery, (snapshot) => {
      const schedulesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSchedules(schedulesData);
    });

    return () => unsubscribe();
  }, [selectedCompany, currentWeek]);

  // Navigate to previous week
  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeek(newDate);
  };

  // Navigate to next week
  const goToNextWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeek(newDate);
  };

  // Get vendor name by ID
  const getVendorName = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor ? vendor.name : 'Unknown Vendor';
  };

  // Get schedule for specific date and meal type
  const getSchedule = (date, mealType) => {
    return schedules.find(s => s.date === date && s.mealType === mealType);
  };

  // Check for vendor conflicts
  const checkVendorConflicts = async (vendorId, date, mealType) => {
    try {
      const conflictQuery = query(
        collection(db, 'MealDeskVendorSchedule'),
        where('vendorId', '==', vendorId),
        where('date', '==', date),
        where('mealType', '==', mealType)
      );
      
      const snapshot = await getDocs(conflictQuery);
      const conflicts = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(schedule => schedule.companyId !== selectedCompany);
      
      return conflicts;
    } catch (error) {
      console.error('Error checking conflicts:', error);
      return [];
    }
  };

  // Get available vendors (non-conflicting)
  const getAvailableVendors = async (date, mealType) => {
    const available = [];
    
    for (const vendor of vendors) {
      const conflicts = await checkVendorConflicts(vendor.id, date, mealType);
      if (conflicts.length === 0) {
        available.push(vendor);
      }
    }
    
    return available;
  };

  // Open assignment modal
  const openAssignmentModal = async (date, mealType) => {
    setSelectedCell({ date, mealType });
    
    // Get available vendors
    const available = await getAvailableVendors(date, mealType);
    setAvailableVendors(available);
    
    // Get conflicting vendors for warning
    const allConflicts = [];
    for (const vendor of vendors) {
      const conflicts = await checkVendorConflicts(vendor.id, date, mealType);
      if (conflicts.length > 0) {
        allConflicts.push({
          vendor,
          conflicts: conflicts.map(c => getCompanyName(c.companyId))
        });
      }
    }
    setConflictVendors(allConflicts);
    
    setShowAssignmentModal(true);
  };

  // Get company name by ID
  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'Unknown Company';
  };

  // Filter companies based on search term
  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(companySearchTerm.toLowerCase())
  );

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
        setShowCompanyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get selected company name
  const getSelectedCompanyName = () => {
    const company = companies.find(c => c.id === selectedCompany);
    return company ? company.name : 'Select Company';
  };

  // Open copy schedule modal
  const openCopyModal = () => {
    if (!selectedCompany) {
      alert('Please select a company first');
      return;
    }
    setCopyFormData({
      fromDate: weekDates[0],
      toDate: weekDates[6],
      targetDate: ''
    });
    setShowCopyModal(true);
  };

  // Copy schedule for date range
  const copySchedule = async () => {
    if (!copyFormData.fromDate || !copyFormData.toDate || !copyFormData.targetDate) {
      alert('Please fill in all date fields');
      return;
    }

    try {
      // Get source schedules
      const sourceQuery = query(
        collection(db, 'MealDeskVendorSchedule'),
        where('companyId', '==', selectedCompany),
        where('date', '>=', copyFormData.fromDate),
        where('date', '<=', copyFormData.toDate),
        orderBy('date')
      );
      
      const sourceSnapshot = await getDocs(sourceQuery);
      const sourceSchedules = sourceSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (sourceSchedules.length === 0) {
        alert('No schedules found for the selected date range');
        return;
      }

      // Calculate date offset
      const targetDate = new Date(copyFormData.targetDate);
      const fromDate = new Date(copyFormData.fromDate);
      const daysOffset = Math.floor((targetDate - fromDate) / (1000 * 60 * 60 * 24));

      // Copy schedules with new dates
      const copyPromises = sourceSchedules.map(async (schedule) => {
        const originalDate = new Date(schedule.date);
        const newDate = new Date(originalDate);
        newDate.setDate(originalDate.getDate() + daysOffset);
        const newDateString = newDate.toISOString().split('T')[0];

        // Check for conflicts before copying
        const conflicts = await checkVendorConflicts(schedule.vendorId, newDateString, schedule.mealType);
        if (conflicts.length > 0) {
          console.warn(`Conflict detected for ${schedule.vendorId} on ${newDateString} for ${schedule.mealType}`);
          return null; // Skip this schedule due to conflict
        }

        // Create new schedule
        const newSchedule = {
          companyId: schedule.companyId,
          vendorId: schedule.vendorId,
          date: newDateString,
          mealType: schedule.mealType,
          createdAt: new Date().toISOString(),
          copiedFrom: schedule.date
        };

        return addDoc(collection(db, 'MealDeskVendorSchedule'), newSchedule);
      });

      const results = await Promise.allSettled(copyPromises);
      const successful = results.filter(result => result.status === 'fulfilled' && result.value !== null).length;
      const failed = results.filter(result => result.status === 'rejected' || result.value === null).length;

      setShowCopyModal(false);
      setCopyFormData({ fromDate: '', toDate: '', targetDate: '' });

      if (successful > 0) {
        alert(`Successfully copied ${successful} schedules${failed > 0 ? ` (${failed} skipped due to conflicts)` : ''}`);
      } else {
        alert('No schedules were copied. All target dates had conflicts.');
      }
    } catch (error) {
      console.error('Error copying schedules:', error);
      alert('Error copying schedules. Please try again.');
    }
  };

  // Assign vendor to schedule
  const assignVendor = async (vendorId) => {
    if (!selectedCell || !selectedCompany) return;

    try {
      const { date, mealType } = selectedCell;
      
      // Check for conflicts
      const conflicts = await checkVendorConflicts(vendorId, date, mealType);
      if (conflicts.length > 0) {
        alert('This vendor is already assigned to another company for this meal and date.');
        return;
      }

      // Check if schedule already exists
      const existingSchedule = getSchedule(date, mealType);
      
      if (existingSchedule) {
        // Update existing schedule
        await updateDoc(doc(db, 'MealDeskVendorSchedule', existingSchedule.id), {
          vendorId,
          updatedAt: new Date()
        });
      } else {
        // Create new schedule
        await addDoc(collection(db, 'MealDeskVendorSchedule'), {
          companyId: selectedCompany,
          vendorId,
          date,
          mealType,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }

      setShowAssignmentModal(false);
      setSelectedCell(null);
    } catch (error) {
      console.error('Error assigning vendor:', error);
      alert('Error assigning vendor. Please try again.');
    }
  };

  // Remove vendor assignment
  const removeAssignment = async (date, mealType) => {
    const schedule = getSchedule(date, mealType);
    if (!schedule) return;

    if (window.confirm('Are you sure you want to remove this assignment?')) {
      try {
        await deleteDoc(doc(db, 'MealDeskVendorSchedule', schedule.id));
      } catch (error) {
        console.error('Error removing assignment:', error);
        alert('Error removing assignment. Please try again.');
      }
    }
  };

  // Get week dates for display
  const weekDates = getWeekDates(currentWeek);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/kerbletmeal/vendors')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Kerblet MealDesk Schedule</h1>
              <p className="text-sm text-gray-500">Plan Meal Schedule For Customers</p>
            </div>
          </div>

          {/* Company Selector and Actions */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Company:</label>
              <div className="relative" ref={companyDropdownRef}>
                <button
                  type="button"
                  onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
                  className="flex items-center justify-between w-64 border border-gray-300 rounded-md px-3 py-2 text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <span className={selectedCompany ? 'text-gray-900' : 'text-gray-500'}>
                    {getSelectedCompanyName()}
                  </span>
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                </button>

                {showCompanyDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                    {/* Search Input */}
                    <div className="p-2 border-b border-gray-200">
                      <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search companies..."
                          value={companySearchTerm}
                          onChange={(e) => setCompanySearchTerm(e.target.value)}
                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                      </div>
                    </div>

                    {/* Company List */}
                    <div className="max-h-48 overflow-y-auto">
                      {filteredCompanies.length > 0 ? (
                        filteredCompanies.map((company) => (
                          <button
                            key={company.id}
                            onClick={() => {
                              setSelectedCompany(company.id);
                              setShowCompanyDropdown(false);
                              setCompanySearchTerm('');
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 hover:text-blue-900 focus:outline-none focus:bg-blue-50 focus:text-blue-900"
                          >
                            {company.name}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No companies found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Copy Schedule Button */}
            <button
              onClick={openCopyModal}
              disabled={!selectedCompany}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy Schedule
            </button>
          </div>
        </div>
      </div>

      {/* Week Navigation */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={goToPreviousWeek}
            className="flex items-center space-x-1 text-gray-600 hover:text-gray-900"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            <span>Previous Week</span>
          </button>
          
          <div className="flex items-center space-x-2">
            <CalendarIcon className="h-4 w-4 text-gray-400" />
            <span className="font-medium">
              {new Date(weekDates[0]).toLocaleDateString()} - {new Date(weekDates[6]).toLocaleDateString()}
            </span>
          </div>
          
          <button
            onClick={goToNextWeek}
            className="flex items-center space-x-1 text-gray-600 hover:text-gray-900"
          >
            <span>Next Week</span>
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Schedule Grid */}
      <div className="flex-1 overflow-y-auto p-6">
        {!selectedCompany ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Please select a company to view the schedule</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {/* Header Row */}
            <div className="grid grid-cols-8 bg-gray-50 border-b border-gray-200">
              <div className="p-3 font-medium text-gray-700">Meal Type</div>
              {daysOfWeek.map((day, index) => (
                <div key={day.key} className="p-3 font-medium text-gray-700 text-center">
                  <div className="text-sm">{day.short}</div>
                  <div className="text-xs text-gray-500">{new Date(weekDates[index]).toLocaleDateString()}</div>
                </div>
              ))}
            </div>

            {/* Meal Type Rows */}
            {mealTypes.map((mealType) => (
              <div key={mealType.key} className="grid grid-cols-8 border-b border-gray-200 last:border-b-0">
                <div className="p-3 bg-gray-50 flex items-center">
                  <span className="mr-2">{mealType.icon}</span>
                  <span className="font-medium text-gray-700">{mealType.label}</span>
                </div>
                
                {daysOfWeek.map((day, dayIndex) => {
                  const date = weekDates[dayIndex];
                  const schedule = getSchedule(date, mealType.key);
                  
                  return (
                    <div key={`${mealType.key}-${day.key}`} className="p-3 border-l border-gray-200 min-h-[80px] flex flex-col">
                      {schedule ? (
                        <div className="flex-1 flex flex-col">
                          <div className="flex-1 bg-blue-50 rounded p-2 mb-2">
                            <div className="text-sm font-medium text-blue-900 truncate">
                              {getVendorName(schedule.vendorId)}
                            </div>
                          </div>
                          <div className="flex justify-end space-x-1">
                            <button
                              onClick={() => openAssignmentModal(date, mealType.key)}
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="Edit assignment"
                            >
                              <PencilIcon className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => removeAssignment(date, mealType.key)}
                              className="text-red-600 hover:text-red-900 p-1"
                              title="Remove assignment"
                            >
                              <TrashIcon className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => openAssignmentModal(date, mealType.key)}
                          className="w-full h-full flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors"
                          title="Assign vendor"
                        >
                          <PlusIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowAssignmentModal(false)}></div>
          
          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <div className="w-screen max-w-md">
              <div className="h-full flex flex-col bg-white shadow-xl">
                {/* Modal header */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">
                      Assign Vendor
                    </h2>
                    <button
                      onClick={() => setShowAssignmentModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedCell && `${new Date(selectedCell.date).toLocaleDateString()} - ${mealTypes.find(m => m.key === selectedCell.mealType)?.label}`}
                  </p>
                </div>

                {/* Modal content */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  {/* Available Vendors */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Available Vendors</h3>
                    <div className="space-y-2">
                      {availableVendors.map((vendor) => (
                        <button
                          key={vendor.id}
                          onClick={() => assignVendor(vendor.id)}
                          className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                        >
                          <div className="font-medium text-gray-900">{vendor.name}</div>
                          <div className="text-sm text-gray-500">{vendor.contactName}</div>
                        </button>
                      ))}
                      {availableVendors.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No available vendors for this time slot
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Conflict Warnings */}
                  {conflictVendors.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                        <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500 mr-1" />
                        Conflicting Assignments
                      </h3>
                      <div className="space-y-2">
                        {conflictVendors.map((conflict, index) => (
                          <div key={index} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="font-medium text-yellow-800">{conflict.vendor.name}</div>
                            <div className="text-sm text-yellow-700">
                              Already assigned to: {conflict.conflicts.join(', ')}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Copy Schedule Modal */}
      {showCopyModal && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCopyModal(false)}></div>
          
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              {/* Modal header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium text-gray-900">
                    Copy Schedule
                  </h2>
                  <button
                    onClick={() => setShowCopyModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Copy schedules from one date range to another
                </p>
              </div>

              {/* Modal content */}
              <div className="px-6 py-4">
                <div className="space-y-4">
                  {/* Source Date Range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Source Date Range
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">From Date</label>
                        <input
                          type="date"
                          value={copyFormData.fromDate}
                          onChange={(e) => setCopyFormData(prev => ({ ...prev, fromDate: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">To Date</label>
                        <input
                          type="date"
                          value={copyFormData.toDate}
                          onChange={(e) => setCopyFormData(prev => ({ ...prev, toDate: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Target Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Start Date
                    </label>
                    <input
                      type="date"
                      value={copyFormData.targetDate}
                      onChange={(e) => setCopyFormData(prev => ({ ...prev, targetDate: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Select target start date"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Schedules will be copied maintaining the same relative dates
                    </p>
                  </div>

                  {/* Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                          Copy Schedule
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <p>• Schedules will be copied to the target date range</p>
                          <p>• Conflicts will be automatically detected and skipped</p>
                          <p>• Original schedules remain unchanged</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCopyModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  onClick={copySchedule}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Copy Schedules
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyScheduleViewer; 